import { Map as MlMap } from "maplibre-gl";
import { getDemPacks } from "../terrain/dem-packs";
import { ensureDemSources, sourceIdFor } from "./webgl-maplibre.dem-source";

const installedLayerIds = new Set<string>();

// Serialize concurrent `showHillshade` calls per active map. Two interleaved
// callers used to collide on `map.addLayer` with the same id: A clears the
// layer set + adds the layer; B awakes, sees an empty set, tries to add the
// same id again → MapLibre throws "layer X already exists" and the
// hillshade toggle promise rejects.
let inflightShow: Promise<void> | null = null;

export function resetHillshadeState(): void {
  installedLayerIds.clear();
  inflightShow = null;
}

/**
 * Returns true when at least one DEM pack is installed locally. Cheap check —
 * does not add anything to the map.
 */
export async function hasHillshadePacks(): Promise<boolean> {
  const packs = await getDemPacks();
  return packs.length > 0;
}

/**
 * Lazy install + show: ensures DEM sources exist, adds ONE hillshade layer
 * per installed pack, and makes them visible. Each pack only paints where
 * its source has tiles, so multi-pack installs composite naturally — a
 * continental cop90-europe (z=4–10) covers the broad view, a regional
 * cop30-at (z=11–12) refines it where coverage overlaps. Picking a single
 * pack would silence the regional pack at high zoom or leave the view
 * unshaded below the regional pack's min_zoom.
 *
 * Layer order: broadest pack (lowest min_zoom) at the bottom, regional pack
 * on top, so high-resolution detail wins where both have coverage. To keep
 * this invariant under late pack arrival (DEM downloads completing mid-
 * session), every call removes any existing hillshade layers and re-adds
 * them in min_zoom-asc order — `addLayer` always appends to the top, so a
 * naive "skip if already installed" path would put a later-arriving broader
 * pack above an earlier-installed regional one, inverting the order.
 */
export async function showHillshade(map: MlMap): Promise<void> {
  // Chain onto any in-flight call so a back-to-back toggle never sees an
  // intermediate state where the layer set is empty but the layers are
  // still installed on the map.
  const prev = inflightShow ?? Promise.resolve();
  const task = prev
    .then(() => doShowHillshade(map))
    .catch((err) => {
      console.warn("[hillshade] showHillshade failed", err);
    });
  inflightShow = task.finally(() => {
    if (inflightShow === task) inflightShow = null;
  });
  return task;
}

async function doShowHillshade(map: MlMap): Promise<void> {
  const packs = await ensureDemSources(map);
  if (packs.length === 0) return;

  for (const layerId of installedLayerIds) {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  }
  installedLayerIds.clear();

  const ordered = [...packs].sort((a, b) => a.manifest.min_zoom - b.manifest.min_zoom);
  for (const pack of ordered) {
    const layerId = `hillshade:${pack.manifest.id}`;
    map.addLayer({
      id: layerId,
      type: "hillshade",
      source: sourceIdFor(pack),
      paint: {
        "hillshade-shadow-color": "#000000",
        "hillshade-highlight-color": "#ffffff",
        "hillshade-accent-color": "#5a5a5a",
        "hillshade-exaggeration": 0.1,
        "hillshade-illumination-anchor": "viewport",
      },
    });
    installedLayerIds.add(layerId);
  }
}

export function hideHillshade(map: MlMap): void {
  for (const layerId of installedLayerIds) {
    if (map.getLayer(layerId)) map.setLayoutProperty(layerId, "visibility", "none");
  }
}
